import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import footerMirrors from "../data/squarespace-footer.json";
import initializeFluidImages from "../utils/initializeFluidImages";
import loadMirrorStylesheet from "../utils/loadMirrorStylesheet";
import SocialIcon from "./SocialIcon";
import { initializeScaledText } from "./SquarespaceMirror";
import "./SiteFooter.css";

const socialLinks = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/10k.science/",
    icon: "instagram",
  },
  {
    label: "Bluesky",
    href: "https://bsky.app/profile/10k.science",
    icon: "bluesky",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/10k-science",
    icon: "linkedin",
  },
];

const supportLogos = [
  {
    label: "500 Global",
    href: "https://500.co/",
    src: "/assets/original/images/500logo.png",
  },
  {
    label: "National Science Foundation",
    href: "https://www.nsf.gov/",
    src: "/assets/original/images/NSF.png",
  },
  {
    label: "National Institutes of Health",
    href: "https://www.nih.gov/",
    src: "/assets/original/images/nih-symbol-white.png",
  },
];

export const prepareFooterForRoute = (root, route) => {
  root.querySelectorAll('a[href="/privacy"]').forEach((privacyLink) => {
    if (privacyLink.parentElement?.querySelector('a[href="/support"]')) {
      return;
    }

    const supportLink = root.ownerDocument.createElement("a");
    supportLink.href = "/support";
    supportLink.textContent = "Support";
    supportLink.setAttribute("target", "_top");
    privacyLink.after(" · ", supportLink);
  });

  if (route !== "/support") return;

  root.querySelectorAll('a[href^="mailto:"]').forEach((emailLink) => {
    emailLink.href = "mailto:info@10k.science";
    emailLink.textContent = "info@10k.science";

    const contactLine = emailLink.closest("p");
    if (contactLine) {
      contactLine.replaceChildren(emailLink);
    }
  });
};

export default function SiteFooter() {
  const hostRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const route =
    location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");
  const footerMirror = footerMirrors[route] || footerMirrors["/"];

  useEffect(() => {
    if (process.env.NODE_ENV === "test") return undefined;

    const controller = new AbortController();
    const host = hostRef.current;
    const shadow = host.shadowRoot || host.attachShadow({ mode: "open" });
    shadow.replaceChildren();

    const renderFooter = async () => {
      const response = await fetch(footerMirror.mirrorUrl, {
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(
          `Unable to load ${footerMirror.mirrorUrl}: ${response.status}`,
        );
      }
      const source = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      if (controller.signal.aborted) return;

      const stylesheetLoads = [];
      for (const node of source.head.querySelectorAll(
        'link[rel="stylesheet"], style',
      )) {
        if (node.tagName === "LINK") {
          const style = document.createElement("style");
          shadow.appendChild(style);
          stylesheetLoads.push(
            loadMirrorStylesheet(node.href).then((css) => {
              if (!controller.signal.aborted) style.textContent = css;
            }),
          );
        } else {
          const style = document.createElement("style");
          style.textContent = node.textContent.replace(/:root\b/g, ":host");
          shadow.appendChild(style);
        }
      }

      const html = document.createElement("html");
      for (const attribute of source.documentElement.attributes) {
        html.setAttribute(attribute.name, attribute.value);
      }
      const body = document.createElement("body");
      for (const attribute of source.body.attributes) {
        body.setAttribute(attribute.name, attribute.value);
      }
      body.innerHTML = source.body.innerHTML;
      body.querySelectorAll("script").forEach((script) => script.remove());
      prepareFooterForRoute(body, route);
      html.appendChild(body);
      shadow.appendChild(html);

      const handleClick = (event) => {
        const anchor = event.target.closest?.("a[href]");
        if (!anchor) return;
        const url = new URL(anchor.href, window.location.href);
        if (
          url.origin === window.location.origin &&
          !anchor.hasAttribute("download")
        ) {
          event.preventDefault();
          navigate(`${url.pathname}${url.search}${url.hash}`);
        }
      };
      shadow.addEventListener("click", handleClick);
      controller.signal.addEventListener(
        "abort",
        () => shadow.removeEventListener("click", handleClick),
        { once: true },
      );

      await Promise.all(stylesheetLoads);
      if (controller.signal.aborted) return;
      const cartFooterSection =
        route === "/cart" ? body.querySelector("footer section") : null;
      const syncCartHeaderSpacing = () => {
        if (!cartFooterSection) return;
        cartFooterSection.style.paddingTop = getComputedStyle(
          document.documentElement,
        ).getPropertyValue("--header-height");
      };
      syncCartHeaderSpacing();
      window.addEventListener("resize", syncCartHeaderSpacing);
      const fluidImages = initializeFluidImages(body);
      const scaledText = initializeScaledText(body);
      scaledText.resize();

      controller.signal.addEventListener(
        "abort",
        () => {
          window.removeEventListener("resize", syncCartHeaderSpacing);
          fluidImages.destroy();
          scaledText.destroy();
        },
        { once: true },
      );
    };

    renderFooter().catch((error) => {
      if (error.name !== "AbortError") console.error(error);
    });
    return () => controller.abort();
  }, [footerMirror.mirrorUrl, navigate, route]);

  return (
    <>
      <div className="site-footer-mirror__host" ref={hostRef} />
      <footer aria-hidden="true" className="site-footer-semantic">
        <div className="site-footer__grid content-width">
          <nav aria-label="Footer navigation" className="site-footer__nav">
            <Link to="/content">Content</Link>
            <Link to="/educators">Educators</Link>
            <Link to="/stories">Stories</Link>
            <a href="https://docs.10k.science" rel="noreferrer">
              Docs
            </a>
            <Link to="/contact">Contact</Link>
          </nav>

          <div className="site-footer__brand">
            <Link aria-label="10k Science home" to="/">
              <img
                alt="10k Science"
                src="/assets/original/brand/10k-science-logo-white.png"
              />
            </Link>
            <nav aria-label="Social media" className="social-links">
              {socialLinks.map((item) => (
                <a
                  aria-label={item.label}
                  href={item.href}
                  key={item.label}
                  rel="noreferrer"
                >
                  <SocialIcon name={item.icon} />
                </a>
              ))}
            </nav>
            <nav aria-label="Legal and support" className="site-footer__legal">
              <Link className="privacy-link" to="/privacy">
                Privacy
              </Link>
              <Link to="/support">Support</Link>
            </nav>
          </div>

          <div className="site-footer__right">
            <div className="site-footer__contact">
              <h4>Contact</h4>
              <a
                href={
                  route === "/support"
                    ? "mailto:info@10k.science"
                    : "mailto:info@dynamoid.com"
                }
              >
                {route === "/support"
                  ? "info@10k.science"
                  : "info@dynamoid.com"}
              </a>
              {route !== "/support" && (
                <span>(510) 646-1304</span>
              )}
            </div>

            <div className="site-footer__support">
              <h4>Made possible with support from</h4>
              <div className="support-logos">
                {supportLogos.map((item) => (
                  <a
                    aria-label={item.label}
                    href={item.href}
                    key={item.label}
                    rel="noreferrer"
                  >
                    <img alt={item.label} src={item.src} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
